import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const WindowWrapper = styled.div`
  position: fixed;
  background: #c0c0c0;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  min-width: 200px;
  min-height: 150px;
  width: ${props => props.isMaximized ? '100%' : props.defaultWidth || '600px'};
  height: ${props => props.isMaximized ? 'calc(100vh - var(--taskbar-height))' : props.defaultHeight || '400px'};
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  resize: ${props => props.isMaximized ? 'none' : 'both'};
  
  @media (max-width: 768px) {
    width: 100% !important;
    height: calc(100% - 80px) !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 40px !important;
    resize: none;
    position: fixed;
  }
`;

const TitleBar = styled.div`
  background: ${props => props.isActive ? '#000080' : '#808080'};
  color: white;
  padding: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  
  @media (max-width: 768px) {
    padding: 8px;
    font-size: 16px;
  }
`;

const TitleText = styled.div`
  flex-grow: 1;
  margin-right: 10px;
`;

const Controls = styled.div`
  display: flex;
  gap: 2px;
`;

const ControlButton = styled.button`
  width: 20px;
  height: 20px;
  background: #c3c3c3;
  border: 1px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;

  &:active {
    border-color: #808080 #ffffff #ffffff #808080;
  }
`;

const WindowContent = styled.div`
  padding: 2px;
  background: white;
  height: calc(100% - 28px);
`;

export const Window = ({
  title,
  children,
  onClose,
  isActive,
  onActivate,
  defaultWidth,
  defaultHeight,
  isMinimized,
  isMaximized,
  onMinimize,
  onMaximize,
  initialPosition
}) => {
  const [position, setPosition] = useState(
    initialPosition 
      ? { 
          x: parseInt(initialPosition.left), 
          y: parseInt(initialPosition.top) 
        }
      : { x: 50, y: 50 }
  );
  const [preMaximizeState, setPreMaximizeState] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Store position and size before maximizing
  useEffect(() => {
    if (isMaximized && !preMaximizeState) {
      setPreMaximizeState({ position, width: defaultWidth, height: defaultHeight });
    } else if (!isMaximized && preMaximizeState) {
      setPosition(preMaximizeState.position);
      setPreMaximizeState(null);
    }
  }, [isMaximized]);

  const handleMouseDown = (e) => {
    if (!isMaximized) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
    onActivate();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && !isMaximized) {
        // Get window dimensions
        const windowWidth = parseInt(defaultWidth) || 600; // Default width if not specified
        const windowHeight = parseInt(defaultHeight) || 400; // Default height if not specified
        
        // Calculate new position with bounds checking
        const newX = Math.min(
          Math.max(0, e.clientX - dragOffset.x), // Don't go past left edge
          window.innerWidth - windowWidth // Don't go past right edge
        );
        
        const newY = Math.min(
          Math.max(0, e.clientY - dragOffset.y), // Don't go past top edge
          window.innerHeight - windowHeight // Don't go past bottom edge
        );

        setPosition({
          x: newX,
          y: newY
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, isMaximized, defaultWidth, defaultHeight]);

  return (
    <WindowWrapper
      isActive={isActive}
      isMinimized={isMinimized}
      isMaximized={isMaximized}
      defaultWidth={defaultWidth}
      defaultHeight={defaultHeight}
      style={{
        left: isMaximized ? 0 : `${position.x}px`,
        top: isMaximized ? 0 : `${position.y}px`,
        display: isMinimized ? 'none' : 'flex',
        zIndex: isActive ? 1000 : 1
      }}
    >
      <TitleBar 
        isActive={isActive}
        onMouseDown={handleMouseDown}
      >
        <TitleText>{title}</TitleText>
        <Controls>
          <ControlButton onClick={onMinimize} title="Minimize">
            _
          </ControlButton>
          <ControlButton onClick={onMaximize} title="Maximize">
            □
          </ControlButton>
          <ControlButton onClick={onClose} title="Close">
            ×
          </ControlButton>
        </Controls>
      </TitleBar>
      <WindowContent>
        {children}
      </WindowContent>
    </WindowWrapper>
  );
}; 